import { tWithKeyAttr as t } from "../../i18nHelper";
import styles from "./usersPage.module.css";
import React, { ChangeEvent, useEffect, useState, useMemo, useCallback } from "react";
import { useAppSelector } from "../../services/hooks";
import { adminSelector } from "../../services/selectors/user.selectors";
import {
  useLazyGetAllCombosForSearchUsersQuery,
  useLazyGetUsersByCompanyIdWithRecommendsQuery,
} from "../../services/api/user.api";
import Modal from "../../components/modals/modal/Modal";
import ErrorPopup from "../../components/modals/errorPopup/errorPopup";
import GeneralSuccessPopup from "../../components/modals/generalSuccessPopup/generalSuccessPopup";
import { userCategories } from "../../utils/categories";
import BackArrowButton from "../../components/backArrowButton/backArrowButton";
import {
  companiesSelector,
  recommendationsSelector,
  rolesSelector,
  usersSelector,
} from "../../services/selectors/users.selectors";
import AddButton from "../../components/addButton/addButton";
import AddNewUserForm from "../../components/forms/addNewUserForm/addNewUserForm";
import UsersList from "../../components/usersList/usersList";
import { filterUsersByString } from "../../utils/search";
import Select from "react-select";
import UsersListStaff from "../../components/usersList/usersListStaff";
import { useGetAllMetaTablesQuery } from "../../services/api/utils.api";
import EditDataPage from "../../components/editUserData/editData";
import { IUserReport } from "../../services/types/users.types";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getUsersStatusOptions,
  usersTreatmentProgramOptions,
  filterSelectStyles,
  TSortOption,
  USER_SORT_OPTIONS,
  USER_STAFF_SORT_OPTIONS,
} from "../../utils/constants";
import CheckboxDropdown from "../../components/checkboxDropdown/checkboxDropdown";
import { appointmentsProgramsDataSelector } from "../../services/selectors/utils.selectors";
import ExcelImportModal from "../../components/modals/excelImportModal/excelImportModal";
import { ROUTES } from "../../utils/routes";
import ExportTableButtons from "../../components/exportTableButtons/ExportTableButtons";
import { resourceQuotasSelector } from "../../services/selectors/entitlements.selectors";

function UsersPage({ usersCategory }: { usersCategory: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const admin = useAppSelector(adminSelector);
  const recommendations = useAppSelector(recommendationsSelector);
  const companies = useAppSelector(companiesSelector);
  const users = useAppSelector(usersSelector);
  const roles = useAppSelector(rolesSelector);
  const appointmentsProgramsData = useAppSelector(
    appointmentsProgramsDataSelector
  );
  const resourceQuotas = useAppSelector(resourceQuotasSelector);
  const seatQuota = resourceQuotas
    ? usersCategory === "clients"
      ? resourceQuotas.patients
      : resourceQuotas.therapists
    : null;
  const isQuotaReached =
    seatQuota !== null &&
    seatQuota.limit !== null &&
    seatQuota.current >= seatQuota.limit;

  const [getAllCombosForSearchUsers] = useLazyGetAllCombosForSearchUsersQuery();
  const [getAllUsersByCompanyIdWithRecommends] =
    useLazyGetUsersByCompanyIdWithRecommendsQuery();

  const [userList, setUserList] = useState(users);

  // עדכון הרשימה המקומית כשהרשימה ב-Redux מתעדכנת
  useEffect(() => {
    setUserList(users);
  }, [users]);
  const [recommendationsIds, setRecommendationsId] = useState<number[]>([]);
  const [companyIds, setCompanyIds] = useState<number[]>([]);
  const [roleIds, setRoleIds] = useState(
    usersCategory === "clients" ? [1, -2] : [-1]
  );
  const [status, setStatus] = useState(-1);
  // TODO: Add filtering logic when user.isDebt field is available from backend
  const [debtFilter, setDebtFilter] = useState(-1); // -1: all, 1: has debt, 0: no debt
  const [treatmentProgramValue, setTreatmentProgramValue] = useState(-1);
  // const [treatmentProgramStatus, setTreatmentProgramStatus] = useState(-1);
  const [fundingSourceIds, setFundingSourceIds] = useState<number[]>([]);
  const [treatmentTypeIds, setTreatmentTypeIds] = useState<number[]>([]);
  const [therapistIds, setTherapistIds] = useState<number[]>([]);
  const [therapistsList, setTherapistsList] = useState<IUserReport[]>([]);
  const [isTherapistsListLoaded, setIsTherapistsListLoaded] = useState(false);
  const [specializationIds, setSpecializationIds] = useState<number[]>([]);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [activeUser, setActiveUser] = useState<IUserReport | undefined>(
    undefined
  );
  const [userToEdit, setUserToEdit] = useState<IUserReport | undefined>(
    undefined
  );
  const [userToEditInitialTab, setUserToEditInitialTab] = useState<'payments' | 'therapists'>('therapists');
  const [showForm, setShowForm] = useState(false);
  const [inputValue, setInputValue] = useState<string>("");
  const [isImportModalOpen, setImportModalOpen] = useState(false);
  const [shouldResetPagination, setShouldResetPagination] =
    useState<boolean>(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [sortBy, setSortBy] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  // Extract selectedUserId from location.state (from financial dashboard)
  const selectedUserId = (location.state as any)?.selectedUserId;
  const fromFinancialDashboard = (location.state as any)?.fromFinancialDashboard;

  useGetAllMetaTablesQuery(admin.companyId);

  // חישוב ערכים יציבים עם useMemo
  const effectiveRoleIds = useMemo(
    () =>
      roleIds.length
        ? roleIds
        : [
          usersCategory === "clients" ? 1 : -1,
          ...(usersCategory === "clients" ? [-2] : []),
        ],
    [roleIds, usersCategory]
  );

  const effectiveFakeCompanyIds = useMemo(
    () =>
      admin?.fakeCompanyId
        ? [admin.fakeCompanyId]
        : companyIds.length
          ? companyIds
          : [-1], // אם לא מסומן כלום → שלח -1
    [admin?.fakeCompanyId, companyIds]
  );

  const effectiveRecommendsIds = useMemo(
    () => (recommendationsIds.length ? recommendationsIds : [0]),
    [recommendationsIds]
  );

  // אם ריק → שלח [0] לשרת (= אין סינון)
  // אחרת → שלח את הערכים שנבחרו
  const effectiveFundingSourceIds = useMemo(
    () => (fundingSourceIds.length > 0 ? fundingSourceIds : [0]),
    [fundingSourceIds]
  );

  const effectiveTreatmentTypeIds = useMemo(
    () => (treatmentTypeIds.length > 0 ? treatmentTypeIds : [0]),
    [treatmentTypeIds]
  );

  const effectiveSpecializationIds = useMemo(
    () => (specializationIds.length > 0 ? specializationIds : [0]),
    [specializationIds]
  );

  const hasTreatmentProgram = useMemo(
    () =>
      treatmentProgramValue === -1
        ? null
        : treatmentProgramValue === 1
          ? true
          : false,
    [treatmentProgramValue]
  );

  // המרת therapistIds ל-GUIDs לשליחה לשרת
  const effectiveTherapistGuids = useMemo(() => {
    if (therapistIds.length === 0) {
      return []; // ריק = אין סינון
    }
    // מחפש את ה-GUIDs לפי ה-IDs שנבחרו
    const guids = therapistIds
      .map((id) => {
        const therapist = therapistsList.find((t) => t.Users.Id === id);
        return therapist?.Users.Guid;
      })
      .filter((guid): guid is string => !!guid);
    return guids;
  }, [therapistIds, therapistsList]);

  const activeFilters = useMemo(() => ({
    companyIds: [admin.companyId],
    roleIds: effectiveRoleIds,
    fakeCompanyIds: effectiveFakeCompanyIds,
    recommendsIds: effectiveRecommendsIds,
    hasTreatmentProgram: hasTreatmentProgram,
    fundingSourceIds: effectiveFundingSourceIds,
    treatmentTypeIds: effectiveTreatmentTypeIds,
    therapistGuids: effectiveTherapistGuids,
    specializationIds: effectiveSpecializationIds,
  }), [
    admin.companyId,
    effectiveRoleIds,
    effectiveFakeCompanyIds,
    effectiveRecommendsIds,
    hasTreatmentProgram,
    effectiveFundingSourceIds,
    effectiveTreatmentTypeIds,
    effectiveTherapistGuids,
    effectiveSpecializationIds,
  ]);

  const refetchUsersTable = useCallback(() => {
    getAllUsersByCompanyIdWithRecommends(activeFilters);
  }, [getAllUsersByCompanyIdWithRecommends, activeFilters]);

  useEffect(() => {
    getAllCombosForSearchUsers(admin.fakeCompanyId ?? admin.companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin.companyId, admin.fakeCompanyId]);

  // קריאה ראשית - טעינת המשתמשים הראשיים
  useEffect(() => {
    // חישוב GUIDs בתוך ה-useEffect רק כש-therapistIds משתנה (לא כש-therapistsList משתנה)
    const therapistGuids =
      therapistIds.length === 0
        ? []
        : therapistIds
          .map((id) => {
            const therapist = therapistsList.find((t) => t.Users.Id === id);
            return therapist?.Users.Guid;
          })
          .filter((guid): guid is string => !!guid);

    getAllUsersByCompanyIdWithRecommends({
      companyIds: [admin.companyId],
      roleIds: effectiveRoleIds,
      fakeCompanyIds: effectiveFakeCompanyIds,
      recommendsIds: effectiveRecommendsIds,
      hasTreatmentProgram: hasTreatmentProgram,
      fundingSourceIds: effectiveFundingSourceIds,
      treatmentTypeIds: effectiveTreatmentTypeIds,
      therapistGuids: therapistGuids,
      specializationIds: effectiveSpecializationIds,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    admin.companyId,
    effectiveRoleIds,
    effectiveFakeCompanyIds,
    effectiveRecommendsIds,
    hasTreatmentProgram,
    effectiveFundingSourceIds,
    effectiveTreatmentTypeIds,
    therapistIds, // רק therapistIds, לא effectiveTherapistGuids!
    effectiveSpecializationIds,
  ]);

  // טעינת רשימת המטפלים רק כשפותחים את ה-dropdown (לא מיד בכניסה לדף)
  const loadTherapistsList = async () => {
    if (
      usersCategory === "clients" &&
      !isTherapistsListLoaded &&
      admin.companyId
    ) {
      setIsTherapistsListLoaded(true);
      const result = await getAllUsersByCompanyIdWithRecommends({
        companyIds: [admin.companyId],
        roleIds: [-1], // מטפלים בלבד
        fakeCompanyIds: admin?.fakeCompanyId ? [admin.fakeCompanyId] : [0],
        recommendsIds: [0],
        // כל הפילטרים ב-0 כדי שהבקאנד יזהה שזו קריאה למטפלים בלבד
        fundingSourceIds: [0],
        treatmentTypeIds: [0],
        therapistGuids: [],
        specializationIds: [0],
      });
      if (result.data?.Users) {
        setTherapistsList(result.data.Users);
      }
    }
  };

  useEffect(() => {
    let filteredUsers = filterUsersByString(inputValue, users);

    // Filter by status
    if (status !== -1) {
      filteredUsers = filteredUsers.filter((u) => u.Users.StatusId === status);
    }

    // Filter by debt
    if (debtFilter !== -1) {
      filteredUsers = filteredUsers.filter((u) => {
        const balance = u.Balance ?? 0;
        if (debtFilter === 1) {
          // Has debt: balance below zero
          return balance < 0;
        } else {
          // No debt: balance zero or above
          return balance >= 0;
        }
      });
    }

    setUserList(filteredUsers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, status, debtFilter]);

  //filter by treatment plan status - waiting to new server logic
  // useEffect(() => {
  //   setUserList(
  //     filterUsersByTreatmentProgramStatus(treatmentProgramStatus, users)
  //   );
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [users, treatmentProgramStatus]);

  // אם יש fakeCompanyId, לא צריך לעדכן את companyIds כי effectiveFakeCompanyIds משתמש בו ישירות
  // useEffect זה נשאר למקרה שצריך לעדכן את companyIds בעתיד

  const onChangeValue = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
    setShouldResetPagination(true);
    setUserList(() => filterUsersByString(event.target.value.trim(), users));
  };

  const handleSortChange = (option: TSortOption | null) => {
    setSortBy(option?.value ?? "");
  };

  const toggleSortDirection = () => {
    setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const activeSortOptions = usersCategory === "therapists" ? USER_STAFF_SORT_OPTIONS : USER_SORT_OPTIONS;

  const sortedUserList = useMemo(() => {
    // Devices (roleId 7) are managed only inside their technician's page — never listed
    // or openable standalone here. Filtered at this single derived value so it holds
    // regardless of which setUserList path (initial / search / refetch) ran.
    const visible = userList.filter((u) => u.Users.RoleId !== 7);
    if (!sortBy || !visible.length) return visible;

    const dir = sortDirection === "asc" ? 1 : -1;
    return [...visible].sort((a, b) => {
      switch (sortBy) {
        case "balance":
          return dir * ((a.Balance ?? 0) - (b.Balance ?? 0));
        case "oldestDebt": {
          // "Debt age": sort by the oldest open-debt charge date (asc = oldest debt first).
          // Users with no open debt (null) always sort last, regardless of direction.
          const aHas = !!a.OldestDebtDate;
          const bHas = !!b.OldestDebtDate;
          if (!aHas && !bHas) return 0;
          if (!aHas) return 1;
          if (!bHas) return -1;
          const aTime = new Date(a.OldestDebtDate as string).getTime();
          const bTime = new Date(b.OldestDebtDate as string).getTime();
          return dir * (aTime - bTime);
        }
        case "availabilityHours":
          return dir * ((a.AvailabilityHours ?? 0) - (b.AvailabilityHours ?? 0));
        case "totalReports":
          return dir * ((a.TotalReports ?? 0) - (b.TotalReports ?? 0));
        case "sessionCost":
          return dir * ((a.Users.SessionCost ?? 0) - (b.Users.SessionCost ?? 0));
        case "roleId":
          return dir * ((a.Users.RoleId ?? 0) - (b.Users.RoleId ?? 0));
        case "dateRegister": {
          return dir * (a.Users.Id - b.Users.Id);
        }
        case "fullName": {
          const aName = (a.Users.FullName || `${a.Users.FirstName} ${a.Users.LastName}`).trim().toLowerCase();
          const bName = (b.Users.FullName || `${b.Users.FirstName} ${b.Users.LastName}`).trim().toLowerCase();
          return dir * aName.localeCompare(bName);
        }
        default:
          return 0;
      }
    });
  }, [userList, sortBy, sortDirection]);
  if (activeUser) {
    return (
      <EditDataPage
        user={activeUser}
        onClose={() => {
          setActiveUser(undefined);
        }}
        activeFilters={activeFilters}
      />
    );
  }

  return (
    <section
      className={styles.users}
      data-testid={
        usersCategory === "clients"
          ? "users-client-section"
          : "users-therapist-section"
      }
    >
      <article className={styles.usersTable}>
        <BackArrowButton
          text={t("PREVIOUS_SCREEN")}
          onClick={() => {
            navigate(ROUTES.USERS);
          }}
          position={"right"}
        />
        <h1 className={`title title_blue`}>
          {usersCategory
            ? t(userCategories.find((c) => c.value === usersCategory)?.label)
            : t("USERS")}
        </h1>
        {!showForm && (
          <div className={styles.addButtonContainer}>
            <AddButton
              onClick={(e) => {
                setShowForm(true);
              }}
              title={t("ADD_NEW_USER")}
              disabled={isQuotaReached}
              tooltip={isQuotaReached ? t("PLAN_SLOT_LIMIT_REACHED") : undefined}
            />
            <AddButton
              onClick={(e) => setImportModalOpen(true)}
              title={t("IMPORT_FROM_EXCEL")}
            />
            <ExportTableButtons
              entity={usersCategory === "clients" ? "UsersClients" : "UsersTherapists"}
              dataRows={sortedUserList?.map(u => {
                const status = u.Users?.StatusId === 1 ? t("ACTIVE") : t("INACTIVE");
                const fullName = u.Users?.FullName || `${u.Users?.FirstName || ""} ${u.Users?.LastName || ""}`.trim();
                if (usersCategory === "clients") {
                  return {
                    Id: u.Users?.Id,
                    FullName: fullName,
                    Phone: u.Users?.Phone || "-",
                    TotalReports: u.TotalReports || "-",
                    Recommends: u.Recommends || "-",
                    Balance: u.Balance ?? "-",
                    Status: status,
                  };
                } else {
                  return {
                    Id: u.Users?.Id,
                    RoleName: u.RoleName || "",
                    FullName: fullName,
                    Phone: u.Users?.Phone || "-",
                    Email: u.Users?.Email || "-",
                    AvailabilityHours: typeof u.AvailabilityHours === "number" ? Math.floor(u.AvailabilityHours) : "-",
                    AppointmentsProgramsNum: u.AppointmentsProgramsNum || "-",
                    Status: status,
                  };
                }
              })}
            />
          </div>
        )}
        {showForm && (
          <AddNewUserForm
            isStaff={usersCategory === "therapists"}
            onClose={() => {
              setShowForm(false);
              setUserToEdit(undefined);
              setUserToEditInitialTab('therapists');
              // Refetch list when closing add form (e.g. after adding new user) so list is up to date; Redux/users.slice will update userList via useEffect
              getAllUsersByCompanyIdWithRecommends({
                companyIds: [admin.companyId],
                roleIds: effectiveRoleIds,
                fakeCompanyIds: effectiveFakeCompanyIds,
                recommendsIds: effectiveRecommendsIds,
                hasTreatmentProgram: hasTreatmentProgram,
                fundingSourceIds: effectiveFundingSourceIds,
                treatmentTypeIds: effectiveTreatmentTypeIds,
                therapistGuids: effectiveTherapistGuids,
                specializationIds: effectiveSpecializationIds,
              });
            }}
            user={userToEdit}
            initialTab={userToEditInitialTab}
            activeFilters={activeFilters}
            type = "createUser"
            onUserUpdated={() => {
              getAllUsersByCompanyIdWithRecommends({
                companyIds: [admin.companyId],
                roleIds: effectiveRoleIds,
                fakeCompanyIds: effectiveFakeCompanyIds,
                recommendsIds: effectiveRecommendsIds,
                hasTreatmentProgram: hasTreatmentProgram,
                fundingSourceIds: effectiveFundingSourceIds,
                treatmentTypeIds: effectiveTreatmentTypeIds,
                therapistGuids: effectiveTherapistGuids,
                specializationIds: effectiveSpecializationIds,
              }).then(result => {
                if (result.data?.Users) {
                  setUserList(result.data.Users);
                  const refreshedUser = result.data.Users.find(
                    (u) => u.Users.Guid === userToEdit?.Users.Guid
                  );
                  if (refreshedUser) setUserToEdit(refreshedUser);
                }
              });
            }}
          />
        )}
        {!showForm && (
          <div className={styles.usersContent}>
            <ul className={styles.usersSearchBar}>
              <li>
                <p className={styles.label}>{t("SEARCH_PLACEHOLDER")}</p>
                <input
                  type={"text"}
                  placeholder={t("SEARCH_PLACEHOLDER")}
                  className={styles.usersInput}
                  data-testid="users-search-input"
                  onChange={onChangeValue}
                  value={inputValue}
                  autoFocus
                />
              </li>
              {usersCategory === "therapists" ? (
                <>
                  <li>
                    <p className={styles.label}>{t("ROLE")}</p>
                    <CheckboxDropdown
                      options={roles.filter((r) => r.value !== 1 && r.value !== 7)}
                      title="roles"
                      selectedValues={roleIds}
                      onChange={setRoleIds}
                      usersCategory={usersCategory}
                    />
                  </li>
                  {admin.fakeCompanyId === null && (
                    <li>
                      <p className={styles.label}>{t("COMPANIES")}</p>
                      <CheckboxDropdown
                        options={companies
                          .filter(
                            (c) =>
                              c.label?.toLowerCase() !== "הכל" &&
                              c.label?.toLowerCase() !== "all" &&
                              c.label?.toUpperCase() !== "ALL"
                          )
                          .map((c) => ({
                            value: c.value ?? 0,
                            label: c.label,
                          }))}
                        title="companies"
                        selectedValues={companyIds}
                        onChange={setCompanyIds}
                        usersCategory={usersCategory}
                      />
                    </li>
                  )}
                  <li>
                    <p className={styles.label}>{t("USER_STATUS")}</p>
                    <Select
                      name={"status"}
                      options={getUsersStatusOptions()}
                      value={getUsersStatusOptions().find(
                        (s) => s.value === status
                      )}
                      formatOptionLabel={(option) => t(option.label)}
                      placeholder={t("SELECT_PLACEHOLDER")}
                      className={styles.select}
                      onChange={(value) => {
                        setStatus(value?.value ?? -1);
                        setShouldResetPagination(true);
                      }}
                    />
                  </li>
                  <li>
                    <p className={styles.label}>{t("DEBT_STATUS")}</p>
                    <Select
                      name={"debtFilter"}
                      options={[
                        { value: -1, label: "ALL" },
                        { value: 1, label: "HAS_DEBT" },
                        { value: 0, label: "NO_DEBT" },
                      ]}
                      value={[
                        { value: -1, label: "ALL" },
                        { value: 1, label: "HAS_DEBT" },
                        { value: 0, label: "NO_DEBT" },
                      ].find((s) => s.value === debtFilter)}
                      formatOptionLabel={(option) => t(option.label)}
                      placeholder={t("SELECT_PLACEHOLDER")}
                      className={styles.select}
                      onChange={(value) => {
                        setDebtFilter(value?.value ?? -1);
                        setShouldResetPagination(true);
                      }}
                    />
                  </li>
                </>
              ) : (
                <>
                  {/* סינונים בסיסיים - שורה ראשונה */}
                  <li>
                    <p className={styles.label}>{t("TREATMENT_PLANS_1")}</p>
                    <Select
                      name={"treatmentProgram"}
                      options={usersTreatmentProgramOptions}
                      value={usersTreatmentProgramOptions.find(
                        (s) => s.value === treatmentProgramValue
                      )}
                      formatOptionLabel={(option) => t(option.label)}
                      placeholder={t("SELECT_PLACEHOLDER")}
                      className={styles.select}
                      onChange={(value) => {
                        setTreatmentProgramValue(value?.value ?? -1);
                      }}
                    />
                  </li>
                  <li>
                    <p className={styles.label}>{t("USER_STATUS")}</p>
                    <Select
                      name={"status"}
                      options={getUsersStatusOptions()}
                      value={getUsersStatusOptions().find(
                        (s) => s.value === status
                      )}
                      formatOptionLabel={(option) => t(option.label)}
                      placeholder={t("SELECT_PLACEHOLDER")}
                      className={styles.select}
                      onChange={(value) => {
                        setStatus(value?.value ?? -1);
                        setShouldResetPagination(true);
                      }}
                    />
                  </li>
                  {admin.fakeCompanyId === null && (
                    <li>
                      <p className={styles.label}>{t("COMPANIES")}</p>
                      <CheckboxDropdown
                        options={companies
                          .filter(
                            (c) =>
                              c.label?.toLowerCase() !== "הכל" &&
                              c.label?.toLowerCase() !== "all" &&
                              c.label?.toUpperCase() !== "ALL"
                          )
                          .map((c) => ({
                            value: c.value ?? 0,
                            label: c.label,
                          }))}
                        title="companies"
                        selectedValues={companyIds}
                        onChange={setCompanyIds}
                        usersCategory={usersCategory}
                      />
                    </li>
                  )}
                  <li>
                    <p className={styles.label}>{t("TREATMENT_TYPE")}</p>
                    <CheckboxDropdown
                      options={
                        appointmentsProgramsData?.TreatmentTypes?.map((tt) => ({
                          value: tt.Id ?? 0,
                          label: tt.Name,
                        })) || []
                      }
                      title="treatmentTypes"
                      selectedValues={treatmentTypeIds}
                      onChange={setTreatmentTypeIds}
                      usersCategory={usersCategory}
                    />
                  </li>
                </>
              )}
            </ul>
            {usersCategory === "clients" && showAdvancedFilters && (
              <ul className={styles.usersSearchBar}>
                {/* סינונים מתקדמים - שורה שנייה */}
                <li>
                  <p className={styles.label}>{t("RECOMMENDATIONS")}</p>
                  <CheckboxDropdown
                    options={recommendations
                      .filter(
                        (r) =>
                          r.label?.toLowerCase() !== "הכל" &&
                          r.label?.toLowerCase() !== "all" &&
                          r.label?.toUpperCase() !== "ALL"
                      )
                      .map((r) => ({
                        value: r.value ?? 0,
                        label: r.label,
                      }))}
                    title="recommendations"
                    selectedValues={recommendationsIds}
                    onChange={setRecommendationsId}
                    usersCategory={usersCategory}
                  />
                </li>
                <li>
                  <p className={styles.label}>{t("FUNDING_SOURCE")}</p>
                  <CheckboxDropdown
                    options={
                      appointmentsProgramsData?.FundingSources?.map((f) => ({
                        value: f.Id ?? 0,
                        label: f.Name,
                      })) || []
                    }
                    title="fundingSources"
                    selectedValues={fundingSourceIds}
                    onChange={setFundingSourceIds}
                    usersCategory={usersCategory}
                  />
                </li>
                <li>
                  <p className={styles.label}>{t("THERAPIST_NAME")}</p>
                  <CheckboxDropdown
                    options={therapistsList.map((therapist) => ({
                      value: therapist.Users.Id,
                      label:
                        `${therapist.Users.FirstName || ""} ${therapist.Users.LastName || ""
                          }`.trim() ||
                        therapist.Users.FullName ||
                        "",
                    }))}
                    title="therapists"
                    selectedValues={therapistIds}
                    onChange={setTherapistIds}
                    usersCategory={usersCategory}
                    showSearch
                    searchPlaceholder={t("SEARCH_THERAPIST")}
                    onOpen={loadTherapistsList}
                  />
                </li>
                <li>
                  <p className={styles.label}>{t("SPECIALIZATION")}</p>
                  <CheckboxDropdown
                    options={
                      appointmentsProgramsData?.TherapistsSpecializations?.map(
                        (s) => ({
                          value: s.Id ?? 0,
                          label: s.Name,
                        })
                      ) || []
                    }
                    title="specializations"
                    selectedValues={specializationIds}
                    onChange={setSpecializationIds}
                    usersCategory={usersCategory}
                  />
                </li>
                <li>
                  <p className={styles.label}>{t("DEBT_STATUS")}</p>
                  <Select
                    name={"debtFilter"}
                    options={[
                      { value: -1, label: "ALL" },
                      { value: 1, label: "HAS_DEBT" },
                      { value: 0, label: "NO_DEBT" },
                    ]}
                    value={[
                      { value: -1, label: "ALL" },
                      { value: 1, label: "HAS_DEBT" },
                      { value: 0, label: "NO_DEBT" },
                    ].find((s) => s.value === debtFilter)}
                    formatOptionLabel={(option) => t(option.label)}
                    placeholder={t("SELECT_PLACEHOLDER")}
                    className={styles.select}
                    onChange={(value) => {
                      setDebtFilter(value?.value ?? -1);
                      setShouldResetPagination(true);
                    }}
                  />
                </li>
              </ul>
            )}
            {usersCategory === "clients" && (
              <div className={styles.advancedFiltersToggle}>
                <button
                  className={`${styles.advancedFiltersButton} hover`}
                  onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                >
                  {showAdvancedFilters
                    ? t("HIDE_ADVANCED_FILTERS")
                    : t("SHOW_ADVANCED_FILTERS")}
                </button>
              </div>
            )}
            <div className={styles.sortRow}>
              <div className={styles.sortSelectWrap}>
                <Select<TSortOption>
                  className={styles.sortSelect}
                  classNamePrefix="filter-select"
                  options={activeSortOptions.map((o) => ({
                    ...o,
                    label: t(o.label) || o.label,
                  }))}
                  value={(() => {
                    const opt = activeSortOptions.find((s) => s.value === sortBy);
                    return opt ? { value: opt.value, label: t(opt.label) || opt.value } : null;
                  })()}
                  onChange={handleSortChange}
                  placeholder={t("REPORT_SORT_BY") || "Sort by"}
                  isSearchable={false}
                  isClearable
                  styles={filterSelectStyles}
                />
              </div>
              <button
                type="button"
                className={styles.sortDirectionBtn}
                onClick={toggleSortDirection}
                disabled={!sortBy}
                title={
                  sortDirection === "asc"
                    ? t("REPORT_SORT_ASC") || "Ascending"
                    : t("REPORT_SORT_DESC") || "Descending"
                }
              >
                {sortDirection === "asc" ? "↑" : "↓"}
              </button>
            </div>

            {users && users.length > 0 ? (
              usersCategory === "clients" ? (
                <UsersList
                  userList={sortedUserList}
                  openUserActionsMenu={setActiveUser}
                  openEditUserForm={(user: IUserReport, initialTab?: 'payments' | 'therapists') => {
                    setUserToEdit(user);
                    setUserToEditInitialTab(initialTab ?? 'therapists');
                    setShowForm(true);
                  }}
                  shouldResetPagination={shouldResetPagination}
                  setShouldResetLimit={setShouldResetPagination}
                  selectedUserId={selectedUserId}
                  fromFinancialDashboard={fromFinancialDashboard}
                  refetchUsersTable={refetchUsersTable}
                />
              ) : (
                <UsersListStaff
                  userList={sortedUserList}
                  openEditUserForm={(user: IUserReport, initialTab?: 'payments' | 'therapists') => {
                    setUserToEdit(user);
                    setUserToEditInitialTab(initialTab ?? 'therapists');
                    setShowForm(true);
                  }}
                  shouldResetPagination={shouldResetPagination}
                  setShouldResetLimit={setShouldResetPagination}
                  refetchUsersTable={refetchUsersTable}
                />
              )
            ) : (
              <p className={styles.noUsers}>{t("NO_USERS_FOUND")}</p>
            )}
            <p className={styles.usersTotal}>
              {t("TOTAL_USERS")}
              {users?.length || "0"}
              {seatQuota && (
                <span className={styles.slotCounter}>
                  {seatQuota.limit === null
                    ? ` (${t("SLOT_USAGE_UNLIMITED")})`
                    : ` (${t("SLOT_USAGE", { used: seatQuota.current, limit: seatQuota.limit })})`}
                  {seatQuota.limit !== null && seatQuota.purchasedSeats > 0 && (
                    <span className={styles.slotSplit}>
                      {t("SLOT_SPLIT", {
                        plan: seatQuota.limit - seatQuota.purchasedSeats,
                        purchased: seatQuota.purchasedSeats,
                      })}
                    </span>
                  )}
                </span>
              )}
            </p>
          </div>
        )}
      </article>

      {openErrorModal && (
        <Modal
          onClose={() => {
            setOpenErrorModal(false);
          }}
        >
          <ErrorPopup
            onClose={() => {
              setOpenErrorModal(false);
            }}
          />
        </Modal>
      )}
      {openSuccessModal && (
        <Modal
          onClose={() => {
            setOpenSuccessModal(false);
            setShowForm(false);

            getAllUsersByCompanyIdWithRecommends({
              companyIds: [admin.companyId],
              roleIds: effectiveRoleIds,
              fakeCompanyIds: effectiveFakeCompanyIds,
              recommendsIds: effectiveRecommendsIds,
              hasTreatmentProgram: hasTreatmentProgram,
              fundingSourceIds: effectiveFundingSourceIds,
              treatmentTypeIds: effectiveTreatmentTypeIds,
              therapistGuids: effectiveTherapistGuids,
              specializationIds: effectiveSpecializationIds,
            });
          }}
        >
          <GeneralSuccessPopup
            onClose={() => {
              setOpenSuccessModal(false);
              setShowForm(false);
              getAllUsersByCompanyIdWithRecommends({
                companyIds: [admin.companyId],
                roleIds: effectiveRoleIds,
                fakeCompanyIds: effectiveFakeCompanyIds,
                recommendsIds: effectiveRecommendsIds,
                hasTreatmentProgram: hasTreatmentProgram,
                fundingSourceIds: effectiveFundingSourceIds,
                treatmentTypeIds: effectiveTreatmentTypeIds,
                therapistGuids: effectiveTherapistGuids,
                specializationIds: effectiveSpecializationIds,
              });
            }}
          />
        </Modal>
      )}

      {isImportModalOpen && (
        <ExcelImportModal
          onClose={() => setImportModalOpen(false)} // מעבירים לו פונקציה שתסגור אותו
          userCategory={usersCategory === "clients" ? "clients" : "therapists"} // מעבירים לו את סוג המשתמש
        />
      )}
    </section>
  );
}

export default UsersPage;
